import Button from "@imminently/decisively-ui/Button";
import Checkbox from "@imminently/decisively-ui/Checkbox";
import Icon from "@imminently/decisively-ui/Icon";
import { notify } from "@/common/notifications";
import { showNotification } from "@/legacy/redux";
import { Label } from "@/ui/primitives/label";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
} from "../components/ComponentPage";

export function ToastsPage() {
  const dispatch = useDispatch();
  const [persistent, setPersistent] = useState(false);
  const toastOptions = persistent ? ({ autoClose: false } as const) : undefined;

  const showLegacyNotification = () => {
    dispatch(showNotification("notification.updated", "info", {
      messageArgs: {
        smart_count: persistent ? false : 3000,
      },
    }));
  };

  const showLoadingToast = () => {
    const id = notify.loading("Saving interview...", {
      autoClose: false,
      closeButton: false,
      draggable: false,
    });

    window.setTimeout(() => {
      notify.update(id, {
        render: "Interview saved",
        type: "success",
        isLoading: false,
        autoClose: persistent ? false : 3000,
        closeButton: true,
        draggable: true,
      });
    }, 1200);
  };

  const showPromiseToast = () => {
    notify.promise(
      new Promise((resolve) => window.setTimeout(resolve, 1200)),
      {
        pending: "Importing release...",
        success: "Release imported",
        error: "Failed to import release",
      },
      toastOptions,
    );
  };

  const persistentToggle = (
    <div className="flex items-center gap-2">
      <Checkbox
        id="toast-persistent"
        checked={persistent}
        onCheckedChange={(checked) => setPersistent(checked === true)}
      />
      <Label htmlFor="toast-persistent">Persistent</Label>
    </div>
  );

  return (
    <ComponentPage
      title="Toasts"
      description="Notification toasts using the app toast container, floating-panel styling, and deci close button."
    >
      <ComponentSection
        title="Types"
        description="Trigger each toast type from the live app notification system."
      >
        <ComponentPreview className="flex-wrap gap-3">
          {persistentToggle}
          <Button
            label="Default toast"
            onClick={() => notify("Default notification", toastOptions)}
          >
            Default
          </Button>
          <Button
            intent="secondary"
            label="Info toast"
            onClick={() => notify.info("Information notification", toastOptions)}
          >
            Info
          </Button>
          <Button
            intent="primary"
            label="Success toast"
            onClick={() => notify.success("Success notification", toastOptions)}
          >
            Success
          </Button>
          <Button
            intent="warning"
            label="Warning toast"
            onClick={() => notify.warning("Warning notification", toastOptions)}
          >
            Warning
          </Button>
          <Button
            intent="destructive"
            label="Error toast"
            onClick={() => notify.error("Error notification", toastOptions)}
          >
            Error
          </Button>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Legacy Notifications"
        description="Trigger the legacy redux notification bridge and translation path."
      >
        <ComponentPreview className="flex-wrap gap-3">
          {persistentToggle}
          <Button
            label="Legacy notification.updated"
            onClick={showLegacyNotification}
          >
            <Icon name="tooltip" />
            notification.updated
          </Button>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Lifecycle"
        description="Exercise loading, promise, update, progress, close button, and auto-close behavior."
      >
        <ComponentPreview className="flex-wrap gap-3">
          {persistentToggle}
          <Button
            label="Loading then update"
            onClick={showLoadingToast}
          >
            <Icon name="save" />
            Loading then update
          </Button>
          <Button
            label="Promise toast"
            onClick={showPromiseToast}
          >
            <Icon name="refresh" />
            Promise
          </Button>
          <Button
            variant="outline"
            label="Long message toast"
            onClick={() => notify("This is a longer toast message so the layout, wrapping, font, close button, and panel shadow can be checked together.", toastOptions)}
          >
            Long message
          </Button>
          <Button
            variant="outline"
            label="Persistent toast"
            onClick={() => notify("Persistent notification", { autoClose: false })}
          >
            Persistent
          </Button>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
